// /emails — list of AI-generated email templates for the active brand.
// Click a card to resume editing (opens /emails/new?id=...). "+ New email"
// CTA at top-right routes to /emails/new with no id (fresh wizard).
//
// M1 scope: tile grid, basic delete, no search/filter. List reads from
// /api/emails which is brand-scoped server-side.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayoutV2 from '../../components/feature/AppLayoutV2';
import { useBrand } from '../../hooks/useBrand';
import { useConfirm } from '../../hooks/useConfirm';
import {
  listEmails,
  deleteEmail,
  EMAIL_CAMPAIGN_META,
  EMAIL_TEMPLATE_META,
  type EmailListItem,
} from '../../lib/api';

export default function EmailsPage() {
  const navigate = useNavigate();
  const { activeBrand } = useBrand();
  const confirm = useConfirm();
  const [emails, setEmails] = useState<EmailListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listEmails()
      .then((rows) => {
        if (!cancelled) setEmails(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load emails.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeBrand?.id]);

  const handleDelete = async (id: string, subject: string | null) => {
    const ok = await confirm({
      title: 'Delete email?',
      message: `Delete "${subject || 'Untitled email'}"? This can't be undone.`,
      destructive: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await deleteEmail(id);
      setEmails((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete email.');
    }
  };

  return (
    <AppLayoutV2>
      <div className="mx-auto px-6 py-8 lg:py-12">

        {/* Header — mirrors /gallery + /templates pattern */}
        <div className="flex items-start justify-between gap-4 mb-8 lg:mb-10">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <i className="ri-mail-line text-2xl" />
            </div>
            <div className="flex-1">
              <h1 className="font-display font-bold text-dark text-2xl lg:text-3xl mb-1">
                Emails
              </h1>
              <p className="text-dark-muted text-sm lg:text-base max-w-xl">
                Generate on-brand email templates for launches, promos, newsletters, and more.
                Edit any section, download as HTML.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/emails/new')}
            className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-colors cursor-pointer"
          >
            <i className="ri-add-line" />
            New email
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm border border-red-200">
            {error}
          </div>
        )}

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-dark-muted">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
            Loading emails…
          </div>
        ) : emails.length === 0 ? (
          <EmptyState onCreate={() => navigate('/emails/new')} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {emails.map((e) => (
              <EmailCard key={e.id} email={e} onOpen={() => navigate(`/emails/new?id=${e.id}`)} onDelete={() => handleDelete(e.id, e.subject)} />
            ))}
          </div>
        )}
      </div>
    </AppLayoutV2>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-surface-dark/40 bg-white py-20 px-6 flex flex-col items-center text-center">
      <div className="w-16 h-16 rounded-2xl bg-primary-light text-primary flex items-center justify-center mb-4">
        <i className="ri-mail-line text-3xl" />
      </div>
      <h3 className="font-display font-bold text-dark text-lg mb-1">
        No emails yet
      </h3>
      <p className="text-sm text-dark-muted max-w-md mb-6">
        Generate your first email in under a minute. Pick a campaign type and template — we'll fill the rest from your brand voice.
      </p>
      <button
        onClick={onCreate}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-colors cursor-pointer"
      >
        <i className="ri-add-line" />
        Create your first email
      </button>
    </div>
  );
}

function EmailCard({
  email, onOpen, onDelete,
}: {
  email: EmailListItem;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const campaign = EMAIL_CAMPAIGN_META.find((c) => c.id === email.campaignType);
  const template = EMAIL_TEMPLATE_META.find((t) => t.id === email.templateId);
  const createdAt = email.createdAt ? new Date(email.createdAt) : null;
  const dateLabel = createdAt
    ? createdAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  return (
    <div className="group relative rounded-2xl border-2 border-surface-dark/30 bg-white p-4 hover:border-primary/40 hover:shadow-md transition-all">
      {/* Hover delete button */}
      <button
        onClick={onDelete}
        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 border border-red-200 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
        title="Delete email"
      >
        <i className="ri-delete-bin-line text-sm" />
      </button>

      <button onClick={onOpen} className="block w-full text-left">
        {/* Hero thumbnail */}
        <div className="aspect-[5/3] rounded-lg bg-surface mb-3 overflow-hidden flex items-center justify-center">
          {email.featuredImageUrl ? (
            // eslint-disable-next-line jsx-a11y/alt-text
            <img src={email.featuredImageUrl} className="w-full h-full object-cover" />
          ) : (
            <div className="flex flex-col items-center gap-1 text-dark-muted/60">
              <i className={`${campaign?.icon || 'ri-mail-line'} text-3xl`} />
              <span className="text-[10px] uppercase tracking-wide font-semibold">
                {campaign?.label || email.campaignType}
              </span>
            </div>
          )}
        </div>

        {/* Subject + meta */}
        <div className="min-w-0">
          <h3 className="font-display font-bold text-dark text-base truncate mb-1">
            {email.subject || 'Untitled email'}
          </h3>
          <div className="flex items-center gap-2 text-xs text-dark-muted">
            <span className="inline-flex items-center gap-1">
              <i className={`${campaign?.icon || 'ri-mail-line'} text-sm`} />
              {campaign?.label || email.campaignType}
            </span>
            <span className="text-dark-muted/40">·</span>
            <span>{template?.name || email.templateId}</span>
          </div>
          {dateLabel && (
            <div className="text-[11px] text-dark-muted/70 mt-1">{dateLabel}</div>
          )}
        </div>
      </button>
    </div>
  );
}
